/**
 * 商品一覧の計測（GA4 / GTM 用 dataLayer プッシュ）
 *
 * - view_item_list : 一覧の中に商品カードが描画されたら、未送信のカードだけを一覧ごとにまとめて送る
 * - select_item    : カード内の商品詳細リンク（/products/）クリックで送る（中クリック含む）
 * - window.fsListAttr(productId) : 直前にクリックした一覧の情報を sessionStorage から返す（view_item / fs_add_to_cart 用）
 *
 * 商品カード: snippets/product-card.liquid の <product-card data-list-item data-item-*>
 * 一覧コンテナ: [data-item-list]（data-list-id / data-list-name / data-list-page / data-page-size / data-list-sort / data-list-filtered）
 *   data-item-list が無い一覧は、所属するセクション（.shopify-section の id）から list_id / list_name を自動で組み立てる。
 */
(() => {
  window.dataLayer = window.dataLayer || [];

  const page = window.fsPageData || {};
  const sent = new WeakSet(); // 送信済みのカード
  const KEY = 'fs_list_attr';
  const TTL = 30 * 60 * 1000;
  const CARD = '[data-list-item]';

  // display:none の中や、非選択のタブ（featured-collections の is-selected 以外）は「描画された」とみなさない
  const isRendered = (el) => {
    if (!el.getClientRects().length) return false;
    const tab = el.closest('.featured-collections-carousel__item');
    if (tab && !tab.classList.contains('is-selected')) return false;
    return getComputedStyle(el).visibility !== 'hidden';
  };

  // 一覧コンテナの解決（明示指定 → 予測検索 → セクション単位の自動判定）
  const sectionKey = (section) =>
    (section.id || '').replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, '') || 'unknown';

  const listOf = (card) => {
    const explicit = card.closest('[data-item-list]');
    if (explicit) return explicit;
    const predictive = card.closest('predictive-search');
    if (predictive) {
      predictive.dataset.itemList = '';
      predictive.dataset.listId = 'predictive_search';
      predictive.dataset.listName = 'search:predictive';
      return predictive;
    }
    const section = card.closest('.shopify-section') || document.body;
    if (!section.hasAttribute('data-item-list')) {
      const key = sectionKey(section);
      section.dataset.itemList = '';
      section.dataset.listId = key;
      section.dataset.listName = `${page.page_type || 'page'}:${key}`;
    }
    return section;
  };

  // PC / SP で出し分けている一覧（片方が display:none）もあるため、描画されているカードだけを数える
  const cardsOf = (list) =>
    [...list.querySelectorAll(CARD)].filter((c) => c.getClientRects().length > 0 && listOf(c) === list);

  // 同じ item_list_name がページ内に複数ある場合、2つ目以降に ":2" ":3" を付ける
  const resolveNames = (lists) => {
    const seen = {};
    lists.forEach((list) => {
      const base = list.dataset.listName || '';
      seen[base] = (seen[base] || 0) + 1;
      list.dataset.fsListName = seen[base] > 1 ? `${base}:${seen[base]}` : base;
    });
  };
  const nameOf = (list) => list.dataset.fsListName || list.dataset.listName || '';

  // 実際のレイアウトから列数を数える（1行目のカードと同じ高さにあるカードの数）
  const columns = (list) => {
    const cards = cardsOf(list).filter(isRendered);
    if (!cards.length) return 1;
    const top = cards[0].getBoundingClientRect().top;
    return cards.filter((c) => Math.abs(c.getBoundingClientRect().top - top) < 2).length || 1;
  };

  const toItem = (card, list, cols) => {
    const cards = cardsOf(list);
    const pageNo = Number(list.dataset.listPage || 1);
    const size = Number(list.dataset.pageSize || cards.length);
    const index = (pageNo - 1) * size + cards.indexOf(card) + 1;
    const item = {
      item_id: card.dataset.itemId,
      item_name: card.dataset.itemName,
      item_brand: card.dataset.itemBrand,
      item_category: card.dataset.itemCategory,
      item_variant: card.dataset.itemVariant,
      price: Number(card.dataset.price),
      index,
      item_row: Math.ceil(index / cols),
      item_available: card.dataset.itemAvailable === 'true',
      item_list_id: list.dataset.listId,
      item_list_name: nameOf(list),
    };
    if (card.dataset.discount) item.discount = Number(card.dataset.discount);
    return item;
  };

  const common = (list, cols) => ({
    list_page: Number(list.dataset.listPage || 1),
    list_sort: list.dataset.listSort || '',
    list_filtered: list.dataset.listFiltered || 'no',
    list_columns: cols,
  });

  const push = (event, list, items, cols) => {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event,
      ...common(list, cols),
      ecommerce: { item_list_id: list.dataset.listId, item_list_name: nameOf(list), items },
    });
  };

  // view_item_list：未送信のカードだけを一覧ごとにまとめて送る
  const flush = () => {
    const lists = [];
    document.querySelectorAll(CARD).forEach((card) => {
      const list = listOf(card);
      if (!lists.includes(list)) lists.push(list);
    });
    resolveNames(lists.filter(isRendered));
    lists.forEach((list) => {
      if (!isRendered(list)) return;
      const cards = cardsOf(list).filter((c) => !sent.has(c) && isRendered(c));
      if (!cards.length) return;
      const cols = columns(list);
      cards.forEach((c) => sent.add(c));
      push('view_item_list', list, cards.map((c) => toItem(c, list, cols)), cols);
    });
  };

  let timer;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(flush, 300);
  };
  // AJAX での一覧差し替え（絞り込み・並べ替え・おすすめ / 最近見た商品の遅延読み込み）と、タブ切り替え（class）を拾う
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'hidden', 'open'],
  });
  document.addEventListener('carousel:change', schedule, true);
  document.addEventListener('carousel:select', schedule, true);
  schedule();

  // select_item
  const onClick = (e) => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const link = target.closest('a[href*="/products/"]');
    if (!link || target.closest('[data-no-select]')) return;
    const card = link.closest(CARD);
    if (!card) return;
    const list = listOf(card);
    const cols = columns(list);
    const item = toItem(card, list, cols);
    push('select_item', list, [item], cols);
    try {
      const now = Date.now();
      const store = JSON.parse(sessionStorage.getItem(KEY) || '{}');
      Object.keys(store).forEach((k) => {
        if (!store[k] || now - store[k].t >= TTL) delete store[k];
      });
      store[String(item.item_id)] = {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        index: item.index,
        t: now,
      };
      sessionStorage.setItem(KEY, JSON.stringify(store));
    } catch (_) {}
  };
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);

  // view_item / fs_add_to_cart 用：直前にクリックした一覧の情報を取り出す
  window.fsListAttr = (productId) => {
    try {
      const a = JSON.parse(sessionStorage.getItem(KEY) || '{}')[String(productId)];
      return a && Date.now() - a.t < TTL
        ? { item_list_id: a.item_list_id, item_list_name: a.item_list_name, index: a.index }
        : {};
    } catch (_) {
      return {};
    }
  };
})();
